import React, { FC } from 'react';
import { Alert, Button, Col, Collapse, Row, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';

import theme from '~/theme';
import { Drawer, StatusField, TextField } from '~/components';
import { Payroll } from '~/types';
import AmountField from '../../../components/fields/AmountField';
import Cell from '~/pages/payrolls/components/Cell';
import PayrollPaymentsTable from '~/pages/payrolls/components/PayrollPaymentsTable';
import { useQuery } from 'react-query';
import { getPayrollsById } from '~/api/payrolls';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { queryClient } from '~/index';
import { payrollResourceKey } from '~/pages/payrolls';

interface Props {
  open: boolean;
  className?: string;
  onClose: () => void;
  data: Payroll | null;
}

const { Panel } = Collapse;

const PayrollShow: FC<Props> = (props) => {
  const { open, className, data: dataFromProps, onClose } = props;

  const [t] = useTranslation('payrolls');

  const payrollId = dataFromProps?.id;

  const {
    data: payrollResponse,
    refetch,
    isFetching,
  } = useQuery(['payroll', payrollId], () => (payrollId ? getPayrollsById(payrollId) : null), {
    enabled: !!payrollId,
  });
  const data = payrollResponse?.data;

  const onRefetchClick = () => {
    refetch().then((result) => {
      if (result?.data?.data?.status === 'COMPLETED') {
        queryClient.refetchQueries(payrollResourceKey);
      }
    });
  };

  return (
    <Drawer
      className={`${className} PayrollShow`}
      title={
        <div>
          <div>{data?.description}</div>
          <div className="PayrollShow-Id">{`${t('translation:ID')} ${data?.id || ''}`}</div>
        </div>
      }
      onClose={onClose}
      open={open}
      size="large"
    >
      {data && (
        <Space direction="vertical" size="large" style={fullWidth}>
          <Space size={16} direction="vertical" style={fullWidth}>
            <Collapse>
              <Panel
                header={<Typography.Text>{t('Transaction details')}</Typography.Text>}
                key={data.id}
              >
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <StatusField data={data.status} />
                  </Col>
                  <Col span={12}>
                    <TextField label={t('Instructed amount')}>
                      <AmountField amount={data.instructedAmount} />
                    </TextField>
                  </Col>
                  <Col span={12}>
                    <TextField label={t('Date and time')}>
                      {moment(data.createdAt).format('LL LT')}
                    </TextField>
                  </Col>
                  <Col span={12}>
                    <TextField label={t('Posted amount')}>
                      <AmountField amount={data.postedAmount} />
                    </TextField>
                  </Col>
                  <Col span={12}>
                    <TextField label={t('Executed by')}>
                      <Cell.Employee record={data} />
                    </TextField>
                  </Col>
                  <Col span={12}>
                    <TextField label={t('Account')}>{data?.senderAccount?.number}</TextField>
                  </Col>
                </Row>
              </Panel>
            </Collapse>
            <div>
              <Typography.Title level={5}>{t('List of records')}</Typography.Title>
              {data.status !== 'COMPLETED_WITH_ERRORS' &&
                data.status !== 'REJECTED' &&
                data.status !== 'COMPLETED' && (
                  <Alert
                    showIcon
                    type="warning"
                    style={{ marginBottom: 8 }}
                    message='Some records are being processed. Click "Refresh" to update statuses'
                    action={
                      <Button
                        size="small"
                        type="text"
                        style={{ color: theme.primaryColor }}
                        onClick={onRefetchClick}
                      >
                        {isFetching ? <LoadingOutlined /> : <ReloadOutlined />}
                        {t('Refresh')}
                      </Button>
                    }
                  />
                )}
              <PayrollPaymentsTable data={data} />
            </div>
          </Space>
        </Space>
      )}
    </Drawer>
  );
};

const fullWidth = { width: '100%' };

export default styled(PayrollShow)`
  .PayrollShow-Id {
    font-size: 10px;
    font-weight: 500;
    color: ${theme.textColorSecondary};
    margin-top: 4px;
  }
`;
